// src/support/enablementWindow.ts
//
// Which fiscal period an Enablement item belongs to. The same rule places a Mindtickle series and a
// journey-map assignment, so the Enablement tab's Time Period dropdown treats them alike: an item sits in the
// period of its DUE date, or the date it was ASSIGNED when it has no due date. An item with neither date cannot
// be placed and is always kept. (The snowflake service applies the same rule to series server-side; journey maps
// are filtered here because their assignment rows are already loaded in full.)

export interface DateWindow {
  /** Inclusive, `YYYY-MM-DD`. */
  start: string;
  end: string;
}

/** The window behind a `range:YYYY-MM-DD:YYYY-MM-DD` time filter, or null for `all` / anything else. */
export function windowFromFilter(timeFilter?: string | null): DateWindow | null {
  const m = /^range:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(String(timeFilter || ''));
  return m ? { start: m[1], end: m[2] } : null;
}

const day = (value?: string | null): string | null => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value || ''));
  return m ? m[1] : null;
};

/** The date that places an item in a period: its due date, else its assigned date. */
export function placementDate(due?: string | null, assigned?: string | null): string | null {
  return day(due) ?? day(assigned);
}

/** Whether an item falls in `window` (inclusive). No window, or no date to place it by, keeps it. */
export function inWindow(window: DateWindow | null, due?: string | null, assigned?: string | null): boolean {
  if (!window) return true;
  const d = placementDate(due, assigned);
  return !d || (window.start <= d && d <= window.end);
}

/** Journey-map assignment rows that fall in the time filter's window. */
export function filterAssignmentsByWindow<T extends { due_date?: string | null; assigned_date?: string | null }>(
  rows: T[],
  timeFilter?: string | null,
): T[] {
  const window = windowFromFilter(timeFilter);
  return window ? rows.filter((r) => inWindow(window, r.due_date, r.assigned_date)) : rows;
}
